import React, { useEffect, useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import Button from '../../components/buttons/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from "yup";
import InputField from '../../components/inputFields/InputField';
import FormikDropdown from '../../components/inputFields/FormikDropDown';
import { Plus, Trash2 } from 'lucide-react';
import { getRawInventoryDropdown, postKitAssemblyView, postKitInventoryCrud, postKitInventoryView } from '../../services/inventory';
import { toast } from 'react-toastify';
import { getUserId } from '../../helpers/local_storage_helpers';
import { searchProductWithAkaName } from '../../services/search';
import AsyncFormikDropdown from '../../components/inputFields/AsyncFormikDropDown';

const KitAssemblyForm = ({ forProduct = false }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [rowData, setRowData] = useState(null);

    // Available components for kit assembly
    const [availableComponents, setAvailableComponents] = useState([]);


    useEffect(() => {
        if (location.state) {
            setRowData(location.state.kit);
        }
    }, [location]);

    const handleDiscardChanges = (resetForm) => {
        resetForm();
        navigate(-1);
    };

    const handleFormSubmit = async (values, { setSubmitting }) => {
        try {
            let data = rowData ? {
                kit: rowData.id,
                time_taken: values.time_taken,
                assembly_cost: values.assembly_cost,
                quantity: values.quantity,
                added_by: getUserId(),
            } : {
                name: values.name,
                description: values.description,
                components: (values.components || [])
                    .filter((c) => c.component != null && String(c.component).trim() !== "")
                    .map((c) => ({
                        component: c.component,
                        quantity: c.quantity,
                    })),
                product: values.product,
                inventory_type: forProduct ? "purchased" : "self_made",
                added_by: getUserId(),
            };

            const res = rowData ? await postKitAssemblyView(data) : (forProduct ? await postKitInventoryCrud(data) : await postKitAssemblyView(data));
            if (res.status === 201) {
                toast.success("Kit assembly created successfully");
                navigate(-1);
            } else {
                toast.error(res.data.error || res.data.detail || res.data);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }

        setSubmitting(false);
    };

    const fetchRawInventoryDropdown = async () => {
        try {
            const res = await getRawInventoryDropdown();
            if (res.status === 200) {
                setAvailableComponents(res.data);
            } else {
                toast.error(res.data);
            }
        } catch (error) {
            toast.error("something went wrong");
        }
    };

    useEffect(() => {
        fetchRawInventoryDropdown();
    }, []);

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: 'Inventory', link: '/inventory/home' },
            { name: 'Kit Assembly', link: '/inventory/kit-assembly' },
            { name: rowData ? (forProduct ? 'Update Product Stock' : 'Update Kit Assembly') : (forProduct ? 'Add Product Stock' : 'Add Kit Assembly') },
        ]} />
        <div className="mt-8">
            <Formik
                initialValues={rowData ? {
                    // time_taken: rowData.time_taken,
                    // assembly_cost: rowData.assembly_cost,
                    quantity: rowData.quantity,
                } : {
                    name: "",
                    sku: "",
                    product: "",
                    description: "",
                    components: []
                }}
                validationSchema={Yup.object(
                    rowData ? {
                        // time_taken: Yup.number().required("Time taken is required"),
                        // assembly_cost: Yup.number().required("Assembly cost is required"),
                        quantity: Yup.number().min(1, "Quantity must be at least 1").required("Quantity is required"),
                    } : forProduct ? {
                        name: Yup.string().required("Product name is required"),
                    } : {
                        name: Yup.string().required("Kit name is required"),
                    })}
                onSubmit={handleFormSubmit}
            >
                {({ isSubmitting, setFieldValue, resetForm, values, errors, touched }) => (
                    <Form>
                        <div className="bg-white shadow-lg rounded-xl border border-gray-200 mt-6 overflow-hidden">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-4">
                                <h2 className="text-xl font-bold text-white">
                                    {rowData ? (forProduct ? 'Update Product Stock' : 'Update Kit Assembly') : (forProduct ? 'Add Product Stock' : 'Add Kit Assembly')}
                                </h2>
                                <p className="text-blue-100 text-sm mt-1">Configure your kit components and assembly details</p>
                            </div>

                            <div className="p-6">
                                {/* Basic Information Section */}
                                {
                                    !rowData &&
                                    <>
                                        <div className="mb-8">
                                            <div className="flex items-center mb-6">
                                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-800">{forProduct ? 'Product Information' : 'Kit Information'}</h3>
                                                    <p className="text-sm text-gray-600">{forProduct ? 'Basic details about your product' : 'Basic details about your kit'}</p>
                                                </div>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-5">
                                                {forProduct ? (
                                                    <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                                                        <AsyncFormikDropdown
                                                            label="Product"
                                                            name="product"
                                                            apiCallFunction={async (input) => {
                                                                const response = await searchProductWithAkaName(input);
                                                                return response.data.map((pro) => ({
                                                                    value: pro.id,
                                                                    label: pro.aka_name ?? pro.product_name,
                                                                    price: pro.price,
                                                                }));
                                                            }}
                                                            onChange={(selected) => {
                                                                setFieldValue(
                                                                    "name",
                                                                    selected?.label ? String(selected.label) : ""
                                                                );
                                                            }}
                                                            needSearch
                                                            className="w-full"
                                                            placeholder="Search product..."
                                                        />
                                                        <InputField
                                                            label="Product Name"
                                                            name="name"
                                                            type="text"
                                                            placeholder="Filled from product aka name"
                                                            required
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                                                        <AsyncFormikDropdown
                                                            label="Product"
                                                            name="product"
                                                            apiCallFunction={async (input) => {
                                                                const response = await searchProductWithAkaName(input);
                                                                return response.data.map((pro) => ({
                                                                    value: pro.id,
                                                                    label: pro.aka_name ?? pro.product_name,
                                                                    price: pro.price,
                                                                }));
                                                            }}
                                                            onChange={(selected) => {
                                                                setFieldValue(
                                                                    "name",
                                                                    selected?.label ? String(selected.label) : ""
                                                                );
                                                            }}
                                                            needSearch
                                                            className="w-full"
                                                            placeholder="Search product..."
                                                        />
                                                        <InputField
                                                            label="Kit Name"
                                                            name="name"
                                                            type="text"
                                                            placeholder="Filled from product aka name"
                                                            required
                                                        />
                                                    </div>
                                                )}
                                                <div className="mt-6">
                                                    <InputField
                                                        label="Description"
                                                        name="description"
                                                        type="text"
                                                        placeholder={forProduct ? "Enter product description" : "Enter kit description"}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {
                                            !forProduct &&
                                            <div className="mb-8">
                                                <div className="flex items-center mb-6">
                                                    <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
                                                        <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-gray-800">Kit Components</h3>
                                                        <p className="text-sm text-gray-600">Optional — add components if this kit uses raw inventory items</p>
                                                    </div>
                                                </div>
                                                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                                    {/* Header */}
                                                    <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b bg-gray-50 font-medium text-sm text-gray-700">
                                                        <div className="col-span-8">Component</div>
                                                        <div className="col-span-3">Quantity</div>
                                                        <div className="col-span-1 text-center">Action</div>
                                                    </div>

                                                    {/* Component Rows */}
                                                    <div className="divide-y divide-gray-100">
                                                        {values.components.length === 0 ? (
                                                            <p className="px-4 py-6 text-sm text-gray-500 text-center">
                                                                No components added. Use &quot;Add Component&quot; below if needed.
                                                            </p>
                                                        ) : null}
                                                        {values.components.map((component, index) => (
                                                            <div key={index} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-gray-50 transition-colors">
                                                                <div className="col-span-8">
                                                                    <FormikDropdown
                                                                        name={`components.${index}.component`}
                                                                        placeholder="Select component"
                                                                        options={availableComponents.map((component) => ({
                                                                            label: component.name,
                                                                            value: component.id,
                                                                        }))}
                                                                        label=""
                                                                    />
                                                                </div>
                                                                <div className="col-span-3">
                                                                    <InputField
                                                                        name={`components.${index}.quantity`}
                                                                        type="number"
                                                                        placeholder="1"
                                                                        min="0.5"
                                                                        step="any"
                                                                        label=""
                                                                    />
                                                                </div>
                                                                <div className="col-span-1 flex justify-center">
                                                                    <Button
                                                                        type="button"
                                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                                        onClick={() => {
                                                                            const newComponents = values.components.filter((_, i) => i !== index);
                                                                            setFieldValue('components', newComponents);
                                                                        }}
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Add Component Button */}
                                                    <div className="px-4 py-3 border-t bg-gray-50">
                                                        <Button
                                                            type="button"
                                                            className="flex items-center gap-2 text-primafrom-primary hover:bg-blue-50 px-3 py-2 rounded-lg border border-blue-200 font-medium transition-colors"
                                                            onClick={() => {
                                                                setFieldValue('components', [...values.components, { component: "", quantity: 1 }]);
                                                            }}
                                                        >
                                                            <Plus size={16} />
                                                            Add Component
                                                        </Button>
                                                    </div>
                                                </div>
                                                {errors.components && touched.components && (
                                                    <p className="text-red-600 text-sm mt-2">
                                                        {typeof errors.components === 'string' ? errors.components : 'Please fix component errors'}
                                                    </p>
                                                )}
                                            </div>
                                        }
                                    </>}


                                {/* Assembly Details Section */}
                                {
                                    rowData &&
                                    <div className="mb-8">
                                        <div className="flex items-center mb-6">
                                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                                                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-800">Assembly Details</h3>
                                                <p className="text-sm text-gray-600">Time and cost information</p>
                                            </div>
                                        </div>
                                        <div className="bg-gray-50 rounded-lg p-5">
                                            <div className="grid gap-6 grid-cols-1 md:grid-cols-4">
                                                {/* <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Expected Assembly Time
                                                    </label>
                                                    <div className="flex gap-3">
                                                        <InputField
                                                            name="time_taken"
                                                            type="number"
                                                            placeholder="Time (in hours)"
                                                            step="any"
                                                        />
                                                    </div>
                                                </div>
                                                <InputField
                                                    label="Assembly Cost (₹)"
                                                    name="assembly_cost"
                                                    type="number"
                                                    placeholder="Enter assembly cost"
                                                    required
                                                /> */}
                                                <InputField
                                                    label="Quantity"
                                                    name="quantity"
                                                    type="number"
                                                    min="1"
                                                    placeholder="Enter quantity"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>}

                                {/* Action Buttons */}
                                <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                                    <Button
                                        type="button"
                                        className="text-gray-700 bg-white hover:bg-gray-50 font-medium rounded-lg px-6 py-2.5 border border-gray-300 transition-colors shadow-sm"
                                        onClick={() => handleDiscardChanges(resetForm)}
                                        disabled={isSubmitting}
                                    >
                                        Discard Changes
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="text-white bg-gradient-to-r from-primary to-primary/80 hover:from-primato-primary/80 hover:to-blue-800 font-medium rounded-lg px-6 py-2.5 transition-all shadow-sm"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Saving..." : "Save Kit Assembly"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div >
    </>);
};

export default KitAssemblyForm; 